import { useRef, useState } from "react";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Clock, ArrowRight, Loader2, CheckCircle2 } from "lucide-react";
import { COMPANY } from "@/data/site";
import { Reveal, MaskedLines } from "@/components/Reveal";
import { usePageMeta } from "@/components/PageHero";

// Same-origin Cloudflare Pages Function (see frontend/functions/api/contact.js).
// Override with REACT_APP_CONTACT_ENDPOINT only if the form is ever hosted elsewhere.
const ENDPOINT = process.env.REACT_APP_CONTACT_ENDPOINT || "/api/contact";

const initial = { first_name: "", last_name: "", email: "", phone: "", comment: "", contact_ref: "" };

const Field = ({ label, name, value, onChange, type = "text", required, testId, textarea }) => (
  <label className="block">
    <span className="font-mono-plex text-sm uppercase tracking-widest text-[#5B6270]">
      {label} {required && <span className="text-[#1C3172]">*</span>}
    </span>
    {textarea ? (
      <textarea
        name={name}
        value={value}
        onChange={onChange}
        required={required}
        rows={5}
        data-testid={testId}
        className="mt-2 w-full bg-white border border-[#D7DCE6] px-4 py-3 text-[#1A1A1A] placeholder-[#9AA3B8] outline-none transition-colors duration-300 focus:border-[#1C3172] resize-none"
      />
    ) : (
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        required={required}
        data-testid={testId}
        className="mt-2 w-full bg-white border border-[#D7DCE6] px-4 py-3 text-[#1A1A1A] placeholder-[#9AA3B8] outline-none transition-colors duration-300 focus:border-[#1C3172]"
      />
    )}
  </label>
);

const Contact = () => {
  usePageMeta(
    "Contact | Book a Crane or Truck | Bass Crane Service, Richmond VA",
    "Need a lift? Contact Bass Crane Service. Call 24/7 at 804-233-0113 to schedule a crane or truck. 1004 Holly Spring Ave., Richmond, VA 23224."
  );

  const [form, setForm] = useState(initial);
  const openedAt = useRef(Date.now());
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState(false);

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(false);
    // Read the actual field values at submit time. Browser autofill and password
    // managers can fill inputs without firing React's onChange, so state alone
    // can be stale or empty.
    const fields = Object.fromEntries(new FormData(e.currentTarget).entries());
    const payload = { ...form, ...fields, elapsed_ms: Date.now() - openedAt.current };
    try {
      const res = await fetch(ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok || !body.ok) throw new Error(body.error || "Request failed");
      setSent(true);
      setForm(initial);
      // Tell GTM a lead went through (only after the worker confirmed OK).
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: "contact_form_success" });
      toast.success("Message sent! We'll be in touch shortly.");
    } catch (err) {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page">
      {/* Photo hero */}
      <section className="relative pt-20 md:pt-[120px] h-[560px] md:h-[720px] flex flex-col justify-center bg-[#0F1D45] text-white overflow-hidden" data-testid="contact-hero">
        <div className="absolute inset-x-0 bottom-0 top-20 md:top-[120px]">
          <img
            src="/images/header-contact.jpg"
            alt="Bass Crane truck crane at dusk with the Richmond skyline behind"
            fetchpriority="high"
            decoding="async"
            className="w-full h-full object-cover object-[65%_center]"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0F1D45]/90 via-[#0F1D45]/50 to-[#0F1D45]/10" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0F1D45]/60 via-transparent to-transparent" />
        </div>
        <div className="relative w-full max-w-[1440px] mx-auto px-6 md:px-12">
          <p className="overline text-white/75 mb-6">[ Contact ]</p>
          <h1 className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl uppercase">
            <MaskedLines lines={["Need a Lift?"]} lineClassName="text-white" />
            <MaskedLines lines={["Contact Us!"]} start={0.25} lineClassName="text-[#9FB4E8]" />
          </h1>
        </div>
      </section>

      <section className="bg-white border-b border-[#E5E7EB]">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 py-16 md:py-24 grid lg:grid-cols-2 gap-12 lg:gap-20">
          {/* Left: info + booking */}
          <div>
            <Reveal>
              <p className="overline text-[#1C3172] mb-4">[ Book a Crane or Truck ]</p>
              <p className="text-[#2B2F38] text-lg leading-relaxed max-w-lg">
                Call us anytime, 24/7, to schedule a crane or truck for your project. We're always
                ready to serve our customers!
              </p>
              <a
                href={COMPANY.phoneHref}
                data-testid="contact-call-big"
                className="group mt-10 flex items-center gap-4 text-[#1A1A1A] hover:text-[#1C3172] transition-colors duration-300"
              >
                <span className="w-14 h-14 grid place-items-center border border-[#D7DCE6] group-hover:border-[#1C3172] transition-colors duration-300">
                  <Phone className="w-6 h-6" />
                </span>
                <span className="font-display text-4xl md:text-5xl uppercase">{COMPANY.phone}</span>
              </a>
            </Reveal>

            <Reveal delay={0.1}>
              <ul className="mt-12 space-y-5 text-[#2B2F38]">
                <li className="flex items-center gap-4">
                  <Mail className="w-5 h-5 text-[#1C3172] shrink-0" />
                  <a href={`mailto:${COMPANY.email}`} className="hover:text-[#1C3172] transition-colors duration-300">{COMPANY.email}</a>
                </li>
                <li className="flex items-start gap-4">
                  <MapPin className="w-5 h-5 text-[#1C3172] shrink-0 mt-1" />
                  <span>{COMPANY.addressLine1}<br />{COMPANY.addressLine2}</span>
                </li>
                <li className="flex items-start gap-4">
                  <Clock className="w-5 h-5 text-[#1C3172] shrink-0 mt-1" />
                  <span>{COMPANY.hours}<br /><span className="text-[#5B6270]">Phone answered 24/7 for scheduling</span></span>
                </li>
              </ul>
            </Reveal>
          </div>

          {/* Right: form */}
          <Reveal delay={0.15}>
            <div className="relative bg-[#F7F8FA] border border-[#E5E7EB] p-8 md:p-10">
              <h2 className="text-2xl md:text-3xl uppercase text-[#1A1A1A] mb-8">Send Us a Message</h2>
              {sent ? (
                <div className="py-10 text-center" data-testid="contact-success" role="status">
                  <CheckCircle2 className="w-12 h-12 text-[#1C3172] mx-auto" strokeWidth={1.5} />
                  <p className="mt-6 font-display text-2xl uppercase text-[#1A1A1A]">Message received</p>
                  <p className="mt-3 text-[#5B6270] leading-relaxed">
                    Thanks! We'll get back to you shortly. Need a crane today?{" "}
                    <a href={COMPANY.phoneHref} className="text-[#1C3172] underline underline-offset-4">Call {COMPANY.phone}</a>, we answer 24/7.
                  </p>
                  <button
                    type="button"
                    onClick={() => setSent(false)}
                    className="mt-8 font-mono-plex text-sm uppercase tracking-widest text-[#1C3172] hover:text-[#2A4FB0]"
                  >
                    Send another message
                  </button>
                </div>
              ) : (
              <form onSubmit={onSubmit} className="space-y-6" data-testid="contact-form" noValidate={false}>
                {/* Honeypot — hidden from people, filled in by bots */}
                <div className="absolute -left-[9999px] top-auto w-px h-px overflow-hidden" aria-hidden="true">
                  <label>
                    Reference
                    <input type="text" name="contact_ref" value={form.contact_ref} onChange={onChange} tabIndex={-1} autoComplete="off" aria-hidden="true" />
                  </label>
                </div>
                <div className="grid sm:grid-cols-2 gap-6">
                  <Field label="First Name" name="first_name" value={form.first_name} onChange={onChange} required testId="contact-first-name" />
                  <Field label="Last Name" name="last_name" value={form.last_name} onChange={onChange} required testId="contact-last-name" />
                </div>
                <Field label="Email" name="email" type="email" value={form.email} onChange={onChange} required testId="contact-email" />
                <Field label="Phone" name="phone" type="tel" value={form.phone} onChange={onChange} testId="contact-phone" />
                <Field label="Comment / Question" name="comment" value={form.comment} onChange={onChange} textarea testId="contact-comment" />
                {error && (
                  <p role="alert" data-testid="contact-error" className="text-sm text-[#B42318] bg-[#FEF3F2] border border-[#FECDCA] px-4 py-3">
                    We couldn't send your message. Please call us at{" "}
                    <a href={COMPANY.phoneHref} className="font-semibold underline underline-offset-2">{COMPANY.phone}</a>{" "}
                    or email <a href={`mailto:${COMPANY.email}`} className="font-semibold underline underline-offset-2">{COMPANY.email}</a>.
                  </p>
                )}
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="contact-submit"
                  className="group w-full inline-flex items-center justify-center gap-3 bg-[#1C3172] text-white px-8 py-5 font-mono-plex uppercase tracking-widest text-sm transition-colors duration-300 hover:bg-[#142457] disabled:opacity-60"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Send <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" /></>}
                </button>
                <p className="text-xs text-[#9AA3B8] leading-relaxed">
                  We'll only use your details to respond to this inquiry.
                </p>
              </form>
              )}
            </div>
          </Reveal>
        </div>
      </section>

      {/* Map */}
      <section data-testid="contact-map" className="bg-white border-b border-[#E5E7EB]">
        <div className="flex items-center gap-4 max-w-[1440px] mx-auto px-6 md:px-12 py-6">
          <MapPin className="w-4 h-4 text-[#1C3172]" />
          <span className="font-mono-plex text-sm uppercase tracking-widest text-[#5B6270]">{COMPANY.address}</span>
        </div>
        <iframe
          title="Bass Crane Service location map"
          src={COMPANY.mapEmbed}
          className="w-full h-[420px] border-t border-[#E5E7EB]"
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
        />
      </section>
    </div>
  );
};

export default Contact;
